import { useState, useCallback, useEffect, useRef } from 'react'
import type { NotebookNote } from '../../shared/types/notebook'
import { DEFAULT_ICON } from './pickNoteEmoji'
import { pickNoteIcon } from './api'

interface UsePageEditorSyncParams {
  note: NotebookNote
  allNotes: NotebookNote[]
  onUpdateNote: (noteId: string, updates: Partial<NotebookNote>) => void
  isNew?: boolean
}

export function usePageEditorSync({ note, allNotes, onUpdateNote, isNew }: UsePageEditorSyncParams) {
  const [editTitle, setEditTitle] = useState(note.title)
  const [editContent, setEditContent] = useState(note.contentMd)
  const titleRef = useRef<HTMLInputElement>(null)

  // Refs for flush-on-unmount (captures latest values for cleanup effect)
  const editTitleRef = useRef(editTitle)
  const editContentRef = useRef(editContent)
  const noteRef = useRef(note)
  const onUpdateNoteRef = useRef(onUpdateNote)

  // Keep refs in sync after each render (used by effects/callbacks, not during render)
  useEffect(() => {
    editTitleRef.current = editTitle
    editContentRef.current = editContent
    noteRef.current = note
    onUpdateNoteRef.current = onUpdateNote
  })

  // Captures pending flush data before the sync block resets local state
  const pendingFlushRef = useRef<{
    noteId: string
    title: string
    contentMd: string
    origTitle: string
    origContentMd: string
    origIcon: string
  } | null>(null)

  // Flag to trigger a server save after temp->server ID swap
  const needsPostSwapSave = useRef(false)

  // Sync local state when note changes (e.g. navigating to a different page)
  const [prevNote, setPrevNote] = useState({ id: note.id, title: note.title, contentMd: note.contentMd })
  if (note.id !== prevNote.id) {
    // Temp->server ID swap: preserve locally-edited content instead of resetting
    const isTempToServer = prevNote.id.startsWith('note-') && !note.id.startsWith('note-')
    if (isTempToServer) {
      // Keep editTitle/editContent as-is -- they have user's in-progress edits
      setPrevNote({ id: note.id, title: editTitle, contentMd: editContent })
      needsPostSwapSave.current = true // eslint-disable-line react-hooks/refs -- flag for post-swap effect
    } else {
      // Capture unsaved edits for the outgoing note before resetting
      if (!prevNote.id.startsWith('note-')) {
        // eslint-disable-next-line react-hooks/refs -- must capture pre-reset values during render
        pendingFlushRef.current = {
          noteId: prevNote.id,
          title: editTitle,
          contentMd: editContent,
          origTitle: prevNote.title,
          origContentMd: prevNote.contentMd,
          origIcon: allNotes.find(n => n.id === prevNote.id)?.icon ?? DEFAULT_ICON,
        }
      }
      // Navigation: fully reset local state to the new note
      setPrevNote({ id: note.id, title: note.title, contentMd: note.contentMd })
      setEditTitle(note.title)
      setEditContent(note.contentMd)
    }
  } else if (note.title !== prevNote.title || note.contentMd !== prevNote.contentMd) {
    // Same note updated from outside (conflict resolution or server-side change)
    setPrevNote({ id: note.id, title: note.title, contentMd: note.contentMd })
    // If content changed externally and local editor hasn't diverged, accept server version
    if (note.contentMd !== prevNote.contentMd && editContent === prevNote.contentMd) {
      setEditContent(note.contentMd)
    }
    if (note.title !== prevNote.title && editTitle === prevNote.title) {
      setEditTitle(note.title)
    }
  }

  // Debounced auto-save for content changes.
  // Only depend on editContent -- use refs for note.id, note.contentMd, onUpdateNote
  // to avoid re-triggering when the parent creates a new notes array from our own save.
  // Note: no temp-ID bail -- handleUpdateNote already skips the API call for temp IDs,
  // and keeping local state in sync ensures the temp->server merge picks up user edits.
  useEffect(() => {
    const n = noteRef.current
    if (editContent === n.contentMd) return
    const timer = setTimeout(() => {
      onUpdateNoteRef.current(noteRef.current.id, { contentMd: editContent })
    }, 1000)
    return () => clearTimeout(timer)
  }, [editContent])

  // Debounced auto-save for title changes.
  // Same ref pattern -- only depend on editTitle.
  useEffect(() => {
    const n = noteRef.current
    const trimmed = editTitle.trim()
    if (!trimmed || trimmed === n.title) return
    const timer = setTimeout(() => {
      const cur = noteRef.current
      const updates: Partial<NotebookNote> = { title: trimmed, summary: trimmed }
      onUpdateNoteRef.current(cur.id, updates)

      // AI icon generation (fire-and-forget) -- capture ID before async gap
      const targetId = cur.id
      if (!targetId.startsWith('note-')) {
        pickNoteIcon(trimmed, editContentRef.current).then(icon => {
          if (icon && icon !== DEFAULT_ICON && noteRef.current.id === targetId) {
            onUpdateNoteRef.current(targetId, { icon })
          }
        }).catch(() => { /* ignore */ })
      }
    }, 1000)
    return () => clearTimeout(timer)
  }, [editTitle])

  // After temp->server ID swap, persist any editor content to the server.
  // The debounce timers above may have fired during the temp phase (updating local
  // state only). This effect ensures the content reaches the server once we have
  // a real ID.
  useEffect(() => {
    if (!needsPostSwapSave.current) return
    needsPostSwapSave.current = false

    const n = noteRef.current
    if (n.id.startsWith('note-')) return // safety -- shouldn't happen

    const content = editContentRef.current
    const title = editTitleRef.current.trim()

    const updates: Partial<NotebookNote> = {}
    if (content) updates.contentMd = content
    if (title) { updates.title = title; updates.summary = title }

    if (Object.keys(updates).length > 0) {
      onUpdateNoteRef.current(n.id, updates)
    }
  }, [note.id])

  // Flush pending edits after navigating away from a page
  useEffect(() => {
    const pending = pendingFlushRef.current
    if (!pending) return
    pendingFlushRef.current = null

    const updates: Partial<NotebookNote> = {}
    const trimmed = pending.title.trim()
    if (trimmed && trimmed !== pending.origTitle) {
      updates.title = trimmed
      updates.summary = trimmed
    }
    if (pending.contentMd !== pending.origContentMd) {
      updates.contentMd = pending.contentMd
    }
    if (Object.keys(updates).length > 0) {
      onUpdateNote(pending.noteId, updates)
    }
  }, [note.id, onUpdateNote])

  // Flush edits on unmount (e.g. leaving notebook screen)
  useEffect(() => {
    return () => {
      // Flush any pending navigation flush
      const pending = pendingFlushRef.current
      if (pending) {
        pendingFlushRef.current = null
        const pUpdates: Partial<NotebookNote> = {}
        const pTrimmed = pending.title.trim()
        if (pTrimmed && pTrimmed !== pending.origTitle) {
          pUpdates.title = pTrimmed
          pUpdates.summary = pTrimmed
        }
        if (pending.contentMd !== pending.origContentMd) {
          pUpdates.contentMd = pending.contentMd
        }
        if (Object.keys(pUpdates).length > 0) {
          onUpdateNoteRef.current(pending.noteId, pUpdates)
        }
      }
      // Flush current note edits
      const n = noteRef.current
      if (n.id.startsWith('note-')) return
      const updates: Partial<NotebookNote> = {}
      const trimmed = editTitleRef.current.trim()
      if (trimmed && trimmed !== n.title) {
        updates.title = trimmed
        updates.summary = trimmed
      }
      if (editContentRef.current !== n.contentMd) {
        updates.contentMd = editContentRef.current
      }
      if (Object.keys(updates).length > 0) {
        onUpdateNoteRef.current(n.id, updates)
      }
    }
  }, [])

  // Auto-focus and select the title for newly created pages
  useEffect(() => {
    if (isNew && titleRef.current) {
      titleRef.current.focus()
      titleRef.current.select()
    }
  }, [isNew])

  const handleTitleBlur = useCallback(() => {
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== note.title) {
      const updates: Partial<NotebookNote> = { title: trimmed, summary: trimmed }
      onUpdateNote(note.id, updates)

      // AI icon generation (fire-and-forget) -- capture ID before async gap
      const targetId = note.id
      pickNoteIcon(trimmed, editContent).then(icon => {
        if (icon && icon !== DEFAULT_ICON && noteRef.current.id === targetId) {
          onUpdateNoteRef.current(targetId, { icon })
        }
      }).catch(() => { /* ignore */ })
    } else if (!trimmed) {
      setEditTitle(note.title)
    }
  }, [editTitle, editContent, note.id, note.title, onUpdateNote])

  const handleTitleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      ;(e.target as HTMLInputElement).blur() // triggers handleTitleBlur -> saves title + icon
      // Focus the always-mounted editor
      setTimeout(() => {
        const editor = document.querySelector<HTMLElement>('[contenteditable]')
        editor?.focus()
      }, 0)
    }
  }, [])

  return {
    editTitle,
    setEditTitle,
    editContent,
    setEditContent,
    titleRef,
    handleTitleBlur,
    handleTitleKeyDown,
  }
}
